<template>
  <v-sheet class="border-opacity-25 border-thin rounded-lg pa-5">
    <v-form ref="resetPasswordForm" v-model="isPasswordFormValid">
      <v-row>
        <v-col>
          <v-text-field
            v-model="resetPasswordFormData.password"
            label="Password"
            :append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showpassword ? 'text' : 'password'"
            :rules="[rules.required, rules.passwordComplexity]"
            :error-messages="passwordErrors.password"
            @click:append="showpassword = !showpassword"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            v-model="resetPasswordFormData.confirmPassword"
            label="Confirm Password"
            :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showConfirmPassword ? 'text' : 'password'"
            :rules="[
              rules.required,
              rules.passwordMatch(
                resetPasswordFormData.password,
                resetPasswordFormData.confirmPassword,
              ),
            ]"
            :error-messages="passwordErrors.confirmPassword"
            @click:append="showConfirmPassword = !showConfirmPassword"></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col v-if="allowClose">
          <v-btn block class="rounded-lg mb-8" elevation="2" @click="closeResetPasswordModal"
            >Cancel</v-btn
          >
        </v-col>
        <v-col>
          <v-btn
            block
            color="primary"
            class="rounded-lg mb-8"
            elevation="2"
            :disabled="!isPasswordFormValid"
            @click="resetPassword"
            >Save</v-btn
          >
        </v-col>
      </v-row>
    </v-form>
  </v-sheet>
</template>
<script setup lang="ts">
import type { VForm } from 'vuetify/components';
import { rules } from '@/helpers';
import { onMounted, ref, toRef, watch } from 'vue';
import { IUserDTO, UserDTO } from '@/dtos';
import { useAuthStore, useUpdateStatusStore, useUserStore } from '@/stores/modules';
import { SNACKSTATUS } from '@/stores/util';

interface Props {
  user?: IUserDTO;
  otp?: string;
  email?: string;
  allowClose: boolean;
}

const props = defineProps<Props>();
const userStore = useUserStore();
const statusStore = useUpdateStatusStore();
const user = ref<IUserDTO>(new UserDTO());
const otp = toRef(props, 'otp', '');
const email = toRef(props, 'email', '');
const allowClose = toRef(props, 'allowClose', true);
const authStore = useAuthStore();
const emit = defineEmits(['passwordResetCompleted', 'passwordResetCanceled', 'snack']);
const isPasswordFormValid = ref<boolean>(false);
const showpassword = ref<boolean>(false);
const showConfirmPassword = ref<boolean>(false);
const resetPasswordForm = ref<InstanceType<typeof VForm> | null>();
const resetPasswordFormData = ref<{ password: string; confirmPassword: string }>({
  password: '',
  confirmPassword: '',
});

const passwordErrors = ref<{ password: string; confirmPassword: string }>({
  password: '',
  confirmPassword: '',
});

const resetPassword = async () => {
  if (resetPasswordForm.value && resetPasswordForm.value.isValid) {
    try {
      await preformReset();
      emit('passwordResetCompleted', true);
    } catch (error: unknown) {
      emit('passwordResetCompleted', false, error as string);
    } finally {
      resetData();
    }
  }
};

const closeResetPasswordModal = () => {
  emit('passwordResetCanceled');
};

function resetData() {
  resetPasswordFormData.value = {
    password: '',
    confirmPassword: '',
  };
  passwordErrors.value = {
    password: '',
    confirmPassword: '',
  };
}

watch(
  () => statusStore.getStatus(`user-${user.value.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `user-${user.value.id}`;
    emit('snack', 'Password', value, statusStore.getError(key));
    statusStore.clearStatus(key);
  },
);

async function preformReset() {
  if (email.value && otp.value) {
    userStore.forgotPassword.mutateAsync({
      code: otp.value,
      email: email.value,
      password: resetPasswordFormData.value.password,
      userId: user.value.id,
    });
  } else {
    userStore.resetPassword.mutateAsync({
      userId: user.value.id,
      password: resetPasswordFormData.value.password,
    });
  }
}
onMounted(() => {
  if (props.user) {
    user.value = new UserDTO(props.user);
  } else if (authStore.isLoggedIn) {
    user.value = new UserDTO(authStore.me);
  }
  console.log('user', user.value);
});
</script>
